import React, { useEffect, useRef, useState } from 'react';
import {
    Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, IconButton,
    LinearProgress, List, ListItem, ListItemText, MenuItem, TextField, Tooltip, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShareIcon from '@mui/icons-material/Share';
import { BOT_API_URL } from './config.js';
import { botJson } from './api.js';
import { isVideoFile } from './file-utils.js';
import { fetchMediaInfo, subtitleTrackLabel } from './subtitle-utils.js';

const EXPIRY_OPTIONS = [
    { value: 1, label: '1 jam' },
    { value: 24, label: '1 hari' },
    { value: 168, label: '7 hari' },
    { value: 720, label: '30 hari' },
    { value: 0, label: 'Tanpa batas waktu' },
];

// Link memakai alamat dari bot (nama Tailscale / Funnel), bukan IP.
export function shareUrlOf(share, bases) {
    if (share.url) return share.url;
    const base = bases && bases.length > 0 ? bases[0].url : BOT_API_URL;
    return `${base}${share.urlPath}`;
}

export async function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(text);
    const area = document.createElement('textarea');
    area.value = text;
    document.body.appendChild(area);
    area.select();
    document.execCommand('copy');
    document.body.removeChild(area);
    return undefined;
}

function formatMb(bytes) {
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// Status "versi Discord" (video yang bisa langsung diputar di Discord).
function EmbedStatus({ embed, isPublic }) {
    if (!embed) return null;
    const { status } = embed;
    if (status === 'ready') {
        const detail = embed.mode === 'original'
            ? 'pakai file asli'
            : `${embed.height ? `${embed.height}p, ` : ''}${embed.size ? formatMb(embed.size) : ''}`;
        return (
            <Typography variant="caption" color={isPublic ? 'success.main' : 'text.secondary'} component="div">
                {isPublic
                    ? `Siap diputar langsung di Discord (${detail}).`
                    : `Versi Discord siap (${detail}), tapi baru tampil di Discord setelah Funnel aktif.`}
            </Typography>
        );
    }
    if (status === 'queued' || status === 'working' || status === 'pending') {
        const percent = typeof embed.progress === 'number' ? Math.round(embed.progress * 100) : null;
        return (
            <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary" component="div">
                    {status === 'working'
                        ? `Menyiapkan video untuk Discord${embed.mode === 'transcode' ? ' (encode ulang supaya muat batas Discord)' : ''}${percent !== null ? ` ${percent}%` : ''}... Kirim link ke Discord setelah ini selesai.`
                        : 'Menunggu giliran menyiapkan video untuk Discord...'}
                </Typography>
                <LinearProgress variant={percent !== null && status === 'working' ? 'determinate' : 'indeterminate'} value={percent || 0} sx={{ mt: 0.5, borderRadius: 1 }} />
            </Box>
        );
    }
    if (status === 'unsupported' || status === 'failed' || status === 'unavailable' || status === 'none') {
        return (
            <Typography variant="caption" color="warning.main" component="div">
                Tidak bisa diputar langsung di Discord{embed.reason ? `: ${embed.reason}` : '.'}
            </Typography>
        );
    }
    return null;
}

// Status versi web (link tanpa versi Discord yang diberi subtitle): video
// dengan subtitle + font ditempel, hanya untuk halaman link.
function WebStatus({ web }) {
    if (!web) return null;
    const { status } = web;
    if (status === 'ready') {
        const detail = [web.height ? `${web.height}p` : null, web.size ? formatMb(web.size) : null].filter(Boolean).join(', ');
        return (
            <Typography variant="caption" color="success.main" component="div">
                Video dengan subtitle siap{detail ? ` (${detail})` : ''}.
            </Typography>
        );
    }
    if (status === 'queued' || status === 'working' || status === 'pending') {
        const percent = typeof web.progress === 'number' ? Math.round(web.progress * 100) : null;
        return (
            <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary" component="div">
                    {status === 'working'
                        ? `Menempel subtitle ke video${percent !== null ? ` ${percent}%` : ''}... Sebelum selesai, link memutar file asli tanpa subtitle.`
                        : 'Menunggu giliran menempel subtitle ke video...'}
                </Typography>
                <LinearProgress variant={percent !== null && status === 'working' ? 'determinate' : 'indeterminate'} value={percent || 0} sx={{ mt: 0.5, borderRadius: 1 }} />
            </Box>
        );
    }
    if (status === 'unsupported' || status === 'failed' || status === 'unavailable' || status === 'none') {
        return (
            <Typography variant="caption" color="warning.main" component="div">
                Subtitle gagal ditempel{web.reason ? `: ${web.reason}` : '.'} Link memutar file asli.
            </Typography>
        );
    }
    return null;
}

export default function ShareDialog({ open, row, fileManager, onClose, onGofile }) {
    const [expires, setExpires] = useState(168);
    const [password, setPassword] = useState('');
    const [embedEnabled, setEmbedEnabled] = useState(true);
    const [subtitle, setSubtitle] = useState('none');
    const [subtitleTracks, setSubtitleTracks] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [created, setCreated] = useState(null);
    const [existing, setExisting] = useState([]);
    const [bases, setBases] = useState([]);
    const [embedLimitMb, setEmbedLimitMb] = useState(75);
    const [copiedId, setCopiedId] = useState(null);
    const pollRef = useRef(null);

    const isVideo = Boolean(row && isVideoFile(row.name));

    const refresh = async ({ fresh = false } = {}) => {
        try {
            const data = await botJson(`/share/${fileManager.userId}${fresh ? '?fresh=1' : ''}`);
            setBases(data.bases || []);
            if (data.embedLimitMb) setEmbedLimitMb(data.embedLimitMb);
            const mine = (data.shares || []).filter((s) => row && s.path === row.path);
            setExisting(mine);
            setCreated((current) => (current ? mine.find((s) => s.id === current.id) || current : current));
            return mine;
        } catch (e) {
            setError(e.message);
            return [];
        }
    };

    useEffect(() => {
        if (!open || !row) return undefined;
        setCreated(null);
        setError(null);
        setPassword('');
        setEmbedEnabled(true);
        setSubtitle('none');
        setSubtitleTracks(null);
        refresh({ fresh: true });
        let cancelled = false;
        if (isVideoFile(row.name)) {
            // Subtitle tertanam yang bisa "dibakar" ke versi Discord (Discord
            // tidak bisa menampilkan subtitle terpisah).
            fetchMediaInfo(fileManager.userId, row.path)
                .then((info) => {
                    if (cancelled) return;
                    const tracks = (info.subtitles || []).filter((t) => t.text);
                    setSubtitleTracks(tracks);
                    const preferred = tracks.find((t) => t.isDefault) || tracks[0];
                    if (preferred) setSubtitle(String(preferred.index));
                })
                .catch(() => { if (!cancelled) setSubtitleTracks([]); });
        }
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, row]);

    // Selama ada versi Discord / versi web yang sedang disiapkan, status diperbarui berkala.
    const preparing = [...existing, ...(created ? [created] : [])]
        .some((s) => [s.embed, s.web].some((v) => v && ['queued', 'working', 'pending'].includes(v.status)));
    useEffect(() => {
        if (!open || !preparing) return undefined;
        pollRef.current = setInterval(() => { refresh(); }, 2500);
        return () => clearInterval(pollRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, preparing]);

    const onCreate = async () => {
        setBusy(true);
        setError(null);
        try {
            const data = await botJson(`/share/${fileManager.userId}`, {
                method: 'POST',
                json: {
                    path: row.path,
                    expiresInHours: expires,
                    password: password || undefined,
                    embed: isVideo ? { enabled: embedEnabled && !password, subtitle: subtitle === 'none' ? null : Number(subtitle) } : undefined,
                },
            });
            setBases(data.bases || bases);
            setCreated(data.share);
            await refresh();
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    const onCopy = async (share) => {
        await copyText(shareUrlOf(share, bases));
        setCopiedId(share.id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const onNativeShare = async (share) => {
        try {
            await navigator.share({ title: share.name, url: shareUrlOf(share, bases) });
        } catch { /* dibatalkan */ }
    };

    const onRevoke = async (share) => {
        if (!window.confirm('Cabut link ini? Siapa pun yang punya link tidak bisa membukanya lagi.')) return;
        try {
            await botJson(`/share/${fileManager.userId}/${share.id}`, { method: 'DELETE' });
            if (created && created.id === share.id) setCreated(null);
            await refresh();
        } catch (e) {
            setError(e.message);
        }
    };

    if (!row) return null;
    const primary = bases[0] || null;
    const isPublic = Boolean(primary && primary.public);
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Bagikan "{row.name}"</DialogTitle>
            <DialogContent>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>Link Disbox (disarankan)</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    File tidak dikirim ke mana pun, cukup dibuatkan link dari PC ini. Video bisa langsung diputar. Link bisa diberi
                    masa berlaku & password, dan bisa dicabut kapan saja.
                </Typography>
                {primary && !isPublic && (
                    <Alert severity="info" sx={{ mb: 1.5 }}>
                        Link sekarang hanya bisa dibuka dari perangkat di Tailscale kamu. Supaya bisa dibuka siapa saja dan video tampil
                        & bisa diputar langsung di Discord, aktifkan Tailscale Funnel di PC bot (lihat README bot, bagian "Link share").
                    </Alert>
                )}
                {!primary && (
                    <Alert severity="warning" sx={{ mb: 1.5 }}>
                        Bot tidak menemukan alamat yang bisa dibuka perangkat lain (Tailscale mati?). Link di bawah mungkin cuma bisa dibuka di PC ini.
                    </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <TextField select size="small" label="Berlaku" value={expires} onChange={(e) => setExpires(Number(e.target.value))} sx={{ minWidth: 160 }}>
                        {EXPIRY_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </TextField>
                    <TextField size="small" type="password" name="disbox-share-password" autoComplete="new-password" label="Password (opsional)" value={password} onChange={(e) => setPassword(e.target.value)} sx={{ flex: 1, minWidth: 160 }} />
                </Box>

                {isVideo && (
                    <Box sx={{ mt: 1.5 }}>
                        <FormControlLabel
                            control={<Checkbox checked={embedEnabled && !password} disabled={Boolean(password)} onChange={(e) => setEmbedEnabled(e.target.checked)} />}
                            label="Bisa diputar langsung di Discord"
                        />
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: -0.5, mb: 1 }}>
                            {password
                                ? 'Link berpassword tidak bisa diputar di Discord (Discord tidak bisa mengisi password).'
                                : `Bot menyiapkan versi video yang bisa diputar Discord (MP4, maks ~${embedLimitMb} MB). Video yang lebih besar di-encode ulang dengan resolusi lebih kecil; link-nya tetap memutar & mengunduh file asli.`}
                        </Typography>
                        {subtitleTracks === null && (
                            <Typography variant="caption" color="text.secondary" component="div">Memeriksa subtitle...</Typography>
                        )}
                        {subtitleTracks && subtitleTracks.length > 0 && (
                            <>
                                <TextField select size="small" fullWidth label={embedEnabled && !password ? 'Subtitle di Discord' : 'Subtitle'} value={subtitle} onChange={(e) => setSubtitle(e.target.value)}>
                                    <MenuItem value="none">Tanpa subtitle</MenuItem>
                                    {subtitleTracks.map((t) => (
                                        <MenuItem key={t.index} value={String(t.index)}>{subtitleTrackLabel(t).replace(/^Tersemat: /, '')} (ditempel ke video)</MenuItem>
                                    ))}
                                </TextField>
                                {!(embedEnabled && !password) && subtitle !== 'none' && (
                                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                                        Halaman link memutar versi 720p dengan subtitle & font ini (sama seperti versi Discord). Bot butuh waktu
                                        kira-kira sepanjang durasi video untuk menyiapkannya; sebelum selesai, link memutar file asli.
                                    </Typography>
                                )}
                            </>
                        )}
                    </Box>
                )}

                <Button variant="contained" onClick={onCreate} disabled={busy} sx={{ mt: 1.5 }} startIcon={busy ? <CircularProgress size={16} /> : <ShareIcon />}>
                    Buat link
                </Button>

                {created && (
                    <Alert severity="success" sx={{ mt: 2 }} action={
                        <Box sx={{ display: 'flex' }}>
                            <Tooltip title="Salin"><IconButton size="small" color="inherit" onClick={() => onCopy(created)}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                            {navigator.share && <Tooltip title="Bagikan"><IconButton size="small" color="inherit" onClick={() => onNativeShare(created)}><ShareIcon fontSize="small" /></IconButton></Tooltip>}
                        </Box>
                    }>
                        <Box component="span" sx={{ wordBreak: 'break-all' }}>{copiedId === created.id ? 'Tersalin! ' : ''}{shareUrlOf(created, bases)}</Box>
                        <EmbedStatus embed={created.embed} isPublic={isPublic} />
                        <WebStatus web={created.web} />
                    </Alert>
                )}
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

                {existing.length > 0 && (
                    <>
                        <Typography variant="subtitle2" sx={{ mt: 2.5 }}>Link yang masih aktif untuk file ini</Typography>
                        <List dense>
                            {existing.map((share) => (
                                <ListItem key={share.id} disableGutters secondaryAction={
                                    <Box>
                                        <Tooltip title="Salin"><IconButton edge="end" size="small" onClick={() => onCopy(share)}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title="Cabut link"><IconButton edge="end" size="small" onClick={() => onRevoke(share)}><DeleteOutlineIcon fontSize="small" color="error" /></IconButton></Tooltip>
                                    </Box>
                                }>
                                    <ListItemText
                                        primary={copiedId === share.id ? 'Tersalin!' : shareUrlOf(share, bases)}
                                        secondary={
                                            <>
                                                {`${share.expiresAt ? `sampai ${new Date(share.expiresAt).toLocaleString('id-ID')}` : 'tanpa batas'}${share.hasPassword ? ' · pakai password' : ''} · dibuka ${share.views}x`}
                                                <EmbedStatus embed={share.embed} isPublic={isPublic} />
                                                <WebStatus web={share.web} />
                                            </>
                                        }
                                        primaryTypographyProps={{ sx: { wordBreak: 'break-all', pr: 7 } }}
                                        secondaryTypographyProps={{ component: 'div' }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </>
                )}

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2">Upload ke GoFile.io</Typography>
                <Typography variant="body2" color="text.secondary">
                    File diupload penuh ke layanan pihak ketiga (GoFile) dan tidak bisa dicabut dari sini.
                    {row.enc ? ' File ini terenkripsi, yang diupload adalah versi yang sudah didekripsi.' : ''}
                </Typography>
                <Button variant="outlined" sx={{ mt: 1 }} onClick={() => { onClose(); onGofile(row); }}>Upload ke GoFile</Button>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Tutup</Button>
            </DialogActions>
        </Dialog>
    );
}
